import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  Eye,
  CheckCircle,
  Users,
  BookOpen,
  Video,
  BarChart3,
  AlertCircle,
  Calendar
} from 'lucide-react';
import {
  getAnalyticsStats,
  getAllAnalytics
} from '../../services/api';
import './Analytics.css';

const Analytics = () => {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [dateRange, setDateRange] = useState('week');

  useEffect(() => {
    fetchAnalyticsData();
  }, [filter, dateRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();

      switch(dateRange) {
        case 'today':
          startDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(startDate.getMonth() - 1);
          break;
        case 'year':
          startDate.setFullYear(startDate.getFullYear() - 1);
          break;
        default:
          startDate.setDate(startDate.getDate() - 7);
      }

      const params = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };

      if (filter !== 'all') {
        params.resourceType = filter;
      }

      // Fetch stats
      const statsResponse = await getAnalyticsStats(params);
      setStats(statsResponse.data.stats);

      // Fetch recent analytics
      const analyticsResponse = await getAllAnalytics({ ...params, limit: 100 });
      setAnalytics(analyticsResponse.data.data || []);

    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError(err.response?.data?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0);
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <AlertCircle size={48} />
        <h2>Error Loading Data</h2>
        <p>{error}</p>
        <button className="btn-retry" onClick={fetchAnalyticsData}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      {/* Header */}
      <div className="analytics-header">
        <div>
          <h1>Platform Analytics</h1>
          <p>Track user engagement and content performance</p>
        </div>
        <div className="analytics-filters">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="filter-select"
          >
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="year">Last Year</option>
          </select>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Content</option>
            <option value="ebook">Ebooks</option>
            <option value="video">Videos</option>
            <option value="chapter">Chapters</option>
            <option value="lesson">Lessons</option>
          </select>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon stat-icon-primary">
            <BarChart3 size={24} />
          </div>
          <div className="stat-content">
            <h3>Total Events</h3>
            <p className="stat-value">{formatNumber(stats?.totalEvents)}</p>
            <span className="stat-label">All interactions</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-info">
            <Eye size={24} />
          </div>
          <div className="stat-content">
            <h3>Total Views</h3>
            <p className="stat-value">{formatNumber(stats?.totalViews)}</p>
            <span className="stat-label">Content views</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-success">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <h3>Completions</h3>
            <p className="stat-value">{formatNumber(stats?.totalCompletions)}</p>
            <span className="stat-label">Completed items</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-warning">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <h3>Active Users</h3>
            <p className="stat-value">{formatNumber(stats?.uniqueUsers)}</p>
            <span className="stat-label">Unique users</span>
          </div>
        </div>
      </div>

      {/* Engagement Metrics */}
      <div className="section-card">
        <div className="section-header">
          <h2>Engagement Metrics</h2>
          <p>Average engagement and time spent</p>
        </div>
        <div className="engagement-grid">
          <div className="engagement-card">
            <Calendar size={20} />
            <div className="engagement-content">
              <span className="engagement-label">Average Duration</span>
              <span className="engagement-value">{formatDuration(stats?.averageDuration || 0)}</span>
            </div>
          </div>
          <div className="engagement-card">
            <TrendingUp size={20} />
            <div className="engagement-content">
              <span className="engagement-label">Total Time Spent</span>
              <span className="engagement-value">{formatDuration(stats?.totalDuration || 0)}</span>
            </div>
          </div>
          <div className="engagement-card">
            <CheckCircle size={20} />
            <div className="engagement-content">
              <span className="engagement-label">Completion Rate</span>
              <span className="engagement-value">
                {stats?.totalViews > 0
                  ? Math.round((stats.totalCompletions / stats.totalViews) * 100)
                  : 0}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content Type Breakdown */}
      {stats?.byResourceType && (
        <div className="section-card">
          <div className="section-header">
            <h2>Content Type Breakdown</h2>
            <p>Performance by content type</p>
          </div>
          <div className="resource-type-grid">
            {Object.entries(stats.byResourceType).map(([type, data]) => (
              <div key={type} className="resource-type-card">
                <div className="resource-type-header">
                  {type === 'ebook' && <BookOpen size={24} className="resource-icon" />}
                  {type === 'video' && <Video size={24} className="resource-icon" />}
                  {type === 'chapter' && <BookOpen size={24} className="resource-icon" />}
                  {type === 'lesson' && <Video size={24} className="resource-icon" />}
                  <h3>{type.charAt(0).toUpperCase() + type.slice(1)}s</h3>
                </div>
                <div className="resource-type-stats">
                  <div className="resource-stat">
                    <span className="resource-stat-label">Total Events</span>
                    <span className="resource-stat-value">{formatNumber(data.total)}</span>
                  </div>
                  <div className="resource-stat">
                    <span className="resource-stat-label">Views</span>
                    <span className="resource-stat-value">{formatNumber(data.views)}</span>
                  </div>
                  <div className="resource-stat">
                    <span className="resource-stat-label">Completions</span>
                    <span className="resource-stat-value">{formatNumber(data.completions)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Resources */}
      {stats?.topResources && stats.topResources.length > 0 && (
        <div className="section-card">
          <div className="section-header">
            <h2>Most Popular Content</h2>
            <p>Top 10 most viewed resources</p>
          </div>
          <div className="top-resources-list">
            {stats.topResources.map((resource, index) => (
              <div key={index} className="top-resource-item">
                <div className="resource-rank">#{index + 1}</div>
                <div className="resource-info">
                  <div className="resource-type-badge">
                    {resource.resourceType === 'ebook' && <BookOpen size={16} />}
                    {resource.resourceType === 'video' && <Video size={16} />}
                    <span>{resource.resourceType}</span>
                  </div>
                  <span className="resource-id">ID: {resource.resourceId}</span>
                </div>
                <div className="resource-views">
                  <Eye size={16} />
                  <span>{formatNumber(resource.views)} views</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="section-card">
        <div className="section-header">
          <h2>Recent Activity</h2>
          <p>Latest user interactions</p>
        </div>
        {analytics.length === 0 ? (
          <div className="empty-state">
            <BarChart3 size={64} strokeWidth={1.5} />
            <h3>No activity data</h3>
            <p>Analytics will appear here once users start engaging with content</p>
          </div>
        ) : (
          <div className="activity-table">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Content Type</th>
                  <th>Action</th>
                  <th>Duration</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {analytics.slice(0, 50).map((item, index) => (
                  <tr key={index}>
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar">
                          {item.user?.name ? item.user.name.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <span className="user-name">{item.user?.name || 'Unknown'}</span>
                      </div>
                    </td>
                    <td>
                      <span className="content-type-badge">{item.resourceType}</span>
                    </td>
                    <td>
                      <span className={`action-badge action-${item.action}`}>
                        {item.action === 'view' && <Eye size={14} />}
                        {item.action === 'complete' && <CheckCircle size={14} />}
                        {item.action}
                      </span>
                    </td>
                    <td>{formatDuration(item.duration)}</td>
                    <td className="time-cell">
                      {new Date(item.timestamp).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
